import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { ROUTES } from '../../consts/general';
import { BIN_RESTOCKING_BRIEF } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import { OUTCOME_ORDER, OUTCOME_STYLE, REASON_LABELS, movesForRecommendation } from '../warehouse/ReplenishmentPanel';
import { formatDate, formatDateTime, formatNumber } from '../warehouse/format';
import useWarehouseEditAccess from '../warehouse/useWarehouseEditAccess';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const pct = (value) => (value == null ? '—' : `${Math.round(Number(value) * 100)}%`);
const emptyPlan = { planDate: null, generatedAt: null, syncFresh: false, summary: { byOutcome: {} }, recommendations: [], planDates: [] };

const UNKNOWN_GROUP_LIMIT = 10;

const unknownPlacementText = (coverage) => {
  const groups = coverage.unknownGroups
    || (coverage.unknownSamples || []).map((code) => ({ label: code, count: 1 }));
  if (!groups.length) return 'none';
  const shown = groups.slice(0, UNKNOWN_GROUP_LIMIT);
  const listed = groups.reduce((sum, group) => sum + group.count, 0);
  const shownCount = shown.reduce((sum, group) => sum + group.count, 0);
  const remaining = Math.max(Number(coverage.unmapped) || 0, listed) - shownCount;
  const text = shown.map((group) => (group.count > 1 ? `${group.label} ×${formatNumber(group.count)}` : group.label)).join(', ');
  return remaining > 0 ? `${text} +${formatNumber(remaining)} more` : text;
};

const BinRestockingReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const canEdit = useWarehouseEditAccess();
  const date = searchParams.get('date') || '';
  const outcomeFilter = searchParams.get('outcome') || 'actionable';
  const [plan, setPlan] = useState(emptyPlan);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams(date ? { date } : {});
      const response = await fetch(`/api/v1/reports/bin-restocking?${query}`, { headers: authHeaders(), signal });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load bin restocking report');
      if (!isLatest(requestId)) return;
      setPlan(json);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, date]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  const updateParams = (next) => {
    const params = { ...Object.fromEntries(searchParams.entries()), ...next };
    Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
    setSearchParams(params);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    setActionError('');
    try {
      const query = new URLSearchParams(plan.planDate ? { date: plan.planDate } : {});
      const response = await fetch(`/api/v1/reports/bin-restocking/export?${query}`, { headers: authHeaders() });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to export bin restocking brief');
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1] || `replenishment-brief-${plan.planDate}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setDownloading(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setActionError('');
    try {
      await axios.post('/api/v1/warehouse/replenishment/run');
      updateParams({ date: '' });
      await load();
    } catch (requestError) {
      setActionError(requestError.response?.data?.error || 'Unable to run the plan');
    } finally {
      setRunning(false);
    }
  };

  const rows = useMemo(() => {
    const all = plan.recommendations || [];
    const filtered = outcomeFilter === 'all'
      ? all
      : outcomeFilter === 'actionable'
        ? all.filter((row) => row.outcome !== 'HEALTHY')
        : all.filter((row) => row.outcome === outcomeFilter);
    return [...filtered].sort((a, b) => {
      const rank = OUTCOME_ORDER.indexOf(a.outcome) - OUTCOME_ORDER.indexOf(b.outcome);
      if (rank !== 0) return rank;
      return (a.fill_pct ?? 1) - (b.fill_pct ?? 1);
    });
  }, [plan, outcomeFilter]);

  const byOutcome = plan.summary?.byOutcome || {};
  const exportButton = (
    <div className="flex items-center gap-2">
      {canEdit && (
        <button type="button" onClick={runNow} disabled={running} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
          {running ? 'Generating…' : 'Generate now'}
        </button>
      )}
      <button type="button" onClick={downloadPdf} disabled={downloading || !plan.planDate} className="rounded-md bg-black px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40">
        {downloading ? 'Preparing PDF…' : 'Download PDF'}
      </button>
    </div>
  );

  return (
    <ReportTemplate
      reportId={BIN_RESTOCKING_BRIEF}
      title="Bin Restocking Brief"
      description="Pickable replenishment recommendations by bin — the same brief the daily Slack/email notification sends, available on demand for any generated plan date."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      exportButton={exportButton}
      renderChildrenOnly
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-gray-600">Plan date</span>
            <select value={date} onChange={(event) => updateParams({ date: event.target.value })} className="h-8 rounded border border-gray-300 bg-white px-2 text-sm">
              {(plan.planDates || []).length === 0 && <option value="">No plans generated</option>}
              {(plan.planDates || []).map((value, index) => <option key={value} value={index === 0 ? '' : value}>{formatDate(value)}{index === 0 ? ' (latest)' : ''}</option>)}
            </select>
          </label>
          <span className="text-gray-500">
            {plan.generatedAt ? `Generated ${formatDateTime(plan.generatedAt)}` : 'Runs each morning after the ShipHero locations sync'}
          </span>
          {plan.planDate && (plan.syncFresh
            ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800">ShipHero sync CURRENT</span>
            : <span className="rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-800">ShipHero sync not fresh — quantities not actionable</span>)}
          {plan.planDate && plan.salesFresh === false && <span className="rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-800">Sales series not current</span>}
          {actionError && <span className="text-red-600">{actionError}</span>}
        </div>
        <div className="grid gap-3 md:grid-cols-6">
          {OUTCOME_ORDER.map((outcome) => (
            <button type="button" key={outcome} onClick={() => updateParams({ outcome: outcomeFilter === outcome ? 'actionable' : outcome })} className={`rounded border bg-white p-3 text-left ${outcomeFilter === outcome ? 'border-gray-900' : ''}`}>
              <div className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${OUTCOME_STYLE[outcome].badge}`}>{outcome}</div>
              <div className="mt-1 text-lg font-semibold">{formatNumber(byOutcome[outcome] ?? 0)}</div>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <button type="button" onClick={() => updateParams({ outcome: 'actionable' })} className={`rounded-full px-3 py-1.5 ${outcomeFilter === 'actionable' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}>Needs action</button>
          <button type="button" onClick={() => updateParams({ outcome: 'all' })} className={`rounded-full px-3 py-1.5 ${outcomeFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}>All evaluated · {formatNumber(plan.recommendations?.length || 0)}</button>
        </div>
        <div className="overflow-auto rounded border bg-white">
          {!plan.planDate ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500">No bin restocking plan has been generated yet.{canEdit ? ' Use Generate now to evaluate the current ShipHero snapshot.' : ''}</div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500">No bins in this group.</div>
          ) : (
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100">
                <tr>
                  {['Outcome', 'Bin', 'SKU', 'Tier', 'Current / Capacity', 'Fill', 'Move', 'From', 'Notes', 'Status'].map((label) => <th key={label} className="whitespace-nowrap px-3 py-2">{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const moves = movesForRecommendation(row);
                  return (
                    <tr key={row.id ?? `${row.location_code}-${row.sku}`} className={index % 2 ? 'bg-gray-50' : ''}>
                      <td className="whitespace-nowrap px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${(OUTCOME_STYLE[row.outcome] || OUTCOME_STYLE.HEALTHY).badge}`}>{row.outcome}</span></td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono font-semibold">{row.location_code}</td>
                      <td className="whitespace-nowrap px-3 py-2">{row.sku}{row.product_name ? <span className="text-gray-500"> · {row.product_name}</span> : null}</td>
                      <td className="whitespace-nowrap px-3 py-2">{row.demand_tier || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{formatNumber(row.current_qty)} / {row.governed_capacity == null ? '?' : formatNumber(row.governed_capacity)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{pct(row.fill_pct)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{row.recommended_qty ? formatNumber(row.recommended_qty) : '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{moves.length ? moves.map((move) => `${move.from} (${formatNumber(move.quantity)})`).join(', ') : '—'}</td>
                      <td className="max-w-md px-3 py-2 text-gray-600">{(row.reasons || []).map((reason) => REASON_LABELS[reason] || reason).join(' · ') || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{row.status === 'open' || !row.status ? 'Open' : `${row.status} · ${row.resolved_by || ''} ${formatDateTime(row.resolved_at)}`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {plan.coverage && (
          <div className="rounded border bg-white p-3 text-xs text-gray-700">
            <div className="font-semibold text-gray-900">Physical map coverage</div>
            <div>{formatNumber(plan.coverage.validated)} validated · {formatNumber(plan.coverage.inferred)} inferred (not used for moves) · {formatNumber(plan.coverage.unmapped)} unknown placement of {formatNumber(plan.coverage.total)} locations</div>
            <div className="text-gray-500">{Object.entries(plan.coverage.byArea || {}).map(([area, entry]) => `${area} ${entry.validated}/${entry.total} validated`).join(' · ')}</div>
            <div className="text-gray-500">Review flags: {Object.entries(plan.coverage.byReview || {}).filter(([, count]) => count).map(([flag, count]) => `${flag} ${count}`).join(', ') || 'none'}</div>
            <div className="text-amber-800">Unknown placement: {unknownPlacementText(plan.coverage)}</div>
          </div>
        )}
        <p className="text-xs text-gray-500">Eadin calculates · humans move in ShipHero · ShipHero zero-level alerts stay on as the emergency backstop.</p>
      </div>
    </ReportTemplate>
  );
};

export default BinRestockingReport;
